import { useEffect, useRef, useState } from 'react'
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Field, Label } from '@headlessui/react'
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import ISO6391 from 'iso-639-1'
import {
  AdManagementService,
  BrandService,
  ChatbotService,
  type AdPage,
  type BookCompetitor,
  type BrandBookRead,
  type BrandColour,
  type BrandLookupMatch,
} from '../client'
import { apiErrorMessage } from '../lib/api'
import { useAuth } from '../providers/auth/useAuth'
import Spinner from '../components/chat/Spinner'
import Modal from '../components/ui/Modal'
import Toggle from '../components/ui/Toggle'

type Book = Required<BrandBookRead>

const MAX_COLOURS = 6
const MAX_LOGOS = 3
const MAX_POINTS = 3
const MAX_COMPETITORS = 5
const MAX_AD_PAGES = 4
const MAX_LANGUAGES = 5
const POLL_MS = 5000
const SEARCH_DELAY = 500
const MIN_QUERY = 2
const NEW_COLOUR = '#a8a29e'
const HEX = /^#[0-9a-f]{6}$/i
const LANGUAGES = ISO6391.getAllCodes().sort((a, b) => ISO6391.getName(a).localeCompare(ISO6391.getName(b)))

const LABEL = 'block text-xs font-medium text-stone-600'
const INPUT =
  'w-full min-w-0 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none'
const TEXTAREA = `${INPUT} resize-y`
const INLINE =
  '-mx-1.5 w-[calc(100%+0.75rem)] rounded-md bg-transparent px-1.5 py-0.5 outline-none placeholder:text-stone-400 hover:bg-stone-50 focus:bg-stone-100'
const ADD =
  'mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-purple-700 transition-colors hover:bg-purple-50'
const REMOVE =
  'flex size-10 shrink-0 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-stone-200 hover:text-stone-700 md:size-9'
const SMALL_REMOVE =
  'flex size-10 shrink-0 items-center justify-center rounded-lg text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 md:size-7'
const TILE_REMOVE =
  'absolute top-2 right-2 flex size-10 items-center justify-center rounded-full bg-white/90 text-stone-600 opacity-0 shadow-sm transition group-hover:opacity-100 hover:text-stone-950 focus-visible:opacity-100 md:size-7 pointer-coarse:opacity-100'
const DASHED =
  'flex cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-dashed border-stone-300 text-sm text-stone-500 transition-colors hover:border-purple-400 hover:text-purple-700'
const PRIMARY =
  'inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-linear-to-b from-stone-700 to-stone-950 px-4 text-sm font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90 disabled:opacity-60'
const CHECKERBOARD = 'bg-[repeating-conic-gradient(#e7e5e4_0_25%,#fff_0_50%)] bg-size-[16px_16px]'

function replaced<T>(list: T[], index: number, item: T) {
  return list.map((current, position) => (position === index ? item : current))
}

function removed<T>(list: T[], index: number) {
  return list.filter((_, position) => position !== index)
}

function PageAvatar({ page }: { page: AdPage }) {
  return (
    <span className="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-stone-200 text-xs text-stone-500">
      {page.name.charAt(0).toUpperCase()}
      {page.profile_picture_url && (
        <img
          src={page.profile_picture_url}
          alt=""
          referrerPolicy="no-referrer"
          onError={(event) => {
            event.currentTarget.hidden = true
          }}
          className="absolute inset-0 size-full object-cover"
        />
      )}
    </span>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-stone-200 py-8 first:border-t-0 first:pt-0">
      <h2 className="font-heading text-lg font-semibold text-stone-900">{title}</h2>
      {hint && <p className="mt-1 text-sm text-stone-500">{hint}</p>}
      <div className="mt-5">{children}</div>
    </section>
  )
}

function TextList({
  label,
  items,
  placeholder,
  onChange,
}: {
  label: string
  items: string[]
  placeholder: string
  onChange: (items: string[]) => void
}) {
  return (
    <div className="mt-6">
      <p className={LABEL}>{label}</p>
      {items.map((item, index) => (
        <div key={index} className="mt-1.5 flex items-center gap-2">
          <textarea
            rows={2}
            value={item}
            placeholder={placeholder}
            onChange={(event) => onChange(replaced(items, index, event.target.value))}
            className={TEXTAREA}
          />
          <button
            type="button"
            aria-label="Remove"
            onClick={() => onChange(removed(items, index))}
            className={REMOVE}
          >
            <XMarkIcon className="size-4" />
          </button>
        </div>
      ))}
      {items.length < MAX_POINTS && (
        <button type="button" onClick={() => onChange([...items, ''])} className={ADD}>
          <PlusIcon className="size-4" />
          Add
        </button>
      )}
    </div>
  )
}

export default function BrandBook() {
  const { brand, refreshAccount } = useAuth()
  const brandId = brand?.id
  const [saved, setSaved] = useState<Book | null>(null)
  const [draft, setDraft] = useState<Book | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pageQuery, setPageQuery] = useState('')
  const [pageMatches, setPageMatches] = useState<BrandLookupMatch[]>([])
  const [searchingPages, setSearchingPages] = useState(false)
  const [addingPage, setAddingPage] = useState(false)
  const [languageQuery, setLanguageQuery] = useState('')
  const searchTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const latestPageSearch = useRef('')
  const generating = saved?.brand_book_generating ?? false

  function show(book: BrandBookRead) {
    setSaved(book as Book)
    setDraft(book as Book)
  }

  function edit<K extends keyof Book>(key: K, value: Book[K]) {
    setDraft((current) => current && { ...current, [key]: value })
  }

  useEffect(() => () => clearTimeout(searchTimer.current), [])

  useEffect(() => {
    if (!brandId) return
    BrandService.getBrandBook(brandId)
      .then(show)
      .catch((err: unknown) => setError(apiErrorMessage(err)))
  }, [brandId])

  useEffect(() => {
    if (!brandId || !generating) return
    const timer = setInterval(() => {
      BrandService.getBrandBook(brandId)
        .then(show)
        .catch(() => undefined)
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [brandId, generating])

  async function save() {
    if (!brandId || !draft) return
    setSaving(true)
    setError(null)
    try {
      show(await BrandService.updateBrandBook(brandId, draft))
      void refreshAccount()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  async function saveEnabled(enabled: boolean) {
    if (!brandId) return
    setError(null)
    try {
      setSaved((await BrandService.updateBrandBook(brandId, { brand_book_enabled: enabled })) as Book)
      edit('brand_book_enabled', enabled)
      void refreshAccount()
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  async function generate() {
    if (!brandId) return
    setError(null)
    try {
      show(await BrandService.generateBrandBook(brandId))
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  function searchPages(query: string) {
    const trimmed = query.trim()
    setPageQuery(query)
    latestPageSearch.current = trimmed
    clearTimeout(searchTimer.current)
    if (!brandId || trimmed.length < MIN_QUERY) {
      setPageMatches([])
      setSearchingPages(false)
      return
    }
    setSearchingPages(true)
    searchTimer.current = setTimeout(() => {
      AdManagementService.lookupBrand(brandId, { query: trimmed })
        .catch(() => ({ matches: [] }))
        .then(({ matches }) => {
          if (latestPageSearch.current !== trimmed) return
          setPageMatches(matches ?? [])
          setSearchingPages(false)
        })
    }, SEARCH_DELAY)
  }

  async function uploadLogo(file: File) {
    if (!brandId) return
    setUploading(true)
    setError(null)
    try {
      const { url } = await ChatbotService.uploadAsset(brandId, { file })
      setDraft((current) => current && { ...current, logos: [...current.logos, url] })
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setUploading(false)
    }
  }

  if (!saved || !draft) {
    return (
      <div className="flex flex-1 items-center justify-center">
        {error ? (
          <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>
        ) : (
          <Spinner className="size-4 border-stone-300 border-t-stone-500" />
        )}
      </div>
    )
  }

  const dirty = JSON.stringify(saved) !== JSON.stringify(draft)
  const badHex = draft.colours.some(({ hex }) => !HEX.test(hex))
  const empty =
    !saved.voice &&
    !saved.style &&
    !saved.extra_info &&
    [saved.colours, saved.logos, saved.ad_pages, saved.selling_points, saved.audiences, saved.competitors].every(
      (list) => list.length === 0,
    )
  const matchingLanguages = LANGUAGES.filter((language) =>
    ISO6391.getName(language).toLowerCase().includes(languageQuery.trim().toLowerCase()),
  )

  return (
    <div className="grow basis-0 overflow-y-auto px-4 py-8 md:px-6">
      <div className="mx-auto max-w-3xl">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Brand Book</h1>
            <p className="mt-1 text-sm text-stone-500">Your brand's details, voice and visual identity</p>
          </div>
          <Field className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-3.5 py-2.5">
            <Label className="cursor-pointer text-sm text-stone-700">Use in chats</Label>
            <Toggle checked={draft.brand_book_enabled} onChange={(enabled) => void saveEnabled(enabled)} />
          </Field>
        </header>

        {error && <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        {generating ? (
          <div className="mt-8">
            <p className="flex items-center gap-2 text-sm text-stone-500">
              <Spinner className="size-3.5 border-stone-300 border-t-purple-600" />
              Building your brand book from {saved.website}…
            </p>
            <div className="mt-6 animate-pulse space-y-4">
              <div className="h-14 rounded-2xl bg-stone-200" />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="h-40 rounded-xl bg-stone-200 last:max-sm:hidden" />
                ))}
              </div>
              <div className="h-24 rounded-xl bg-stone-200" />
              <div className="h-24 rounded-xl bg-stone-200" />
            </div>
          </div>
        ) : (
          <>
            {empty && (
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-purple-200 bg-purple-50 px-5 py-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-900">
                    Fill this in from {saved.website || 'your website'}
                  </p>
                  <p className="mt-0.5 text-sm text-stone-600">
                    {saved.website
                      ? 'Reads your site and researches the brand. Takes under a minute.'
                      : 'Add your website under Basics and save first.'}
                  </p>
                </div>
                <button type="button" disabled={!saved.website || dirty} onClick={() => void generate()} className={PRIMARY}>
                  <SparklesIcon className="size-4" />
                  Generate from website
                </button>
              </div>
            )}

            <div className="mt-8">
              <Section title="Basics">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={LABEL}>
                    Name
                    <input value={draft.name} onChange={(event) => edit('name', event.target.value)} className={`${INPUT} mt-1.5`} />
                  </label>
                  <label className={LABEL}>
                    Website
                    <input
                      value={draft.website ?? ''}
                      placeholder="https://"
                      onChange={(event) => edit('website', event.target.value)}
                      className={`${INPUT} mt-1.5`}
                    />
                  </label>
                  <Field className="relative">
                    <Label className={LABEL}>Languages</Label>
                    <Combobox
                      multiple
                      immediate
                      value={draft.languages}
                      onChange={(languages: string[]) => {
                        edit('languages', languages)
                        setLanguageQuery('')
                      }}
                      onClose={() => setLanguageQuery('')}
                    >
                      <div className="mt-1.5 flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2 py-1 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100">
                        {draft.languages.map((language) => (
                          <span
                            key={language}
                            className="inline-flex items-center gap-0.5 rounded-md bg-stone-100 py-0.5 pr-0.5 pl-2 text-sm text-stone-800"
                          >
                            {ISO6391.getName(language) || language}
                            <button
                              type="button"
                              aria-label={`Remove ${ISO6391.getName(language) || language}`}
                              onClick={() => edit('languages', draft.languages.filter((current) => current !== language))}
                              className="flex size-8 items-center justify-center rounded text-stone-400 md:size-5 transition-colors hover:bg-stone-200 hover:text-stone-700"
                            >
                              <XMarkIcon className="size-3.5" />
                            </button>
                          </span>
                        ))}
                        <ComboboxInput
                          value={languageQuery}
                          placeholder={
                            draft.languages.length >= MAX_LANGUAGES
                              ? `Up to ${MAX_LANGUAGES}`
                              : draft.languages.length > 0
                                ? 'Add'
                                : 'Pick languages'
                          }
                          onChange={(event) => setLanguageQuery(event.target.value)}
                          className="min-w-16 flex-1 bg-transparent px-1 py-0.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none"
                        />
                      </div>
                      <ComboboxOptions className="absolute top-full left-0 z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-stone-200 bg-white p-1 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] [scrollbar-width:thin] empty:invisible">
                        {matchingLanguages.map((language) => (
                          <ComboboxOption
                            key={language}
                            value={language}
                            disabled={draft.languages.length >= MAX_LANGUAGES && !draft.languages.includes(language)}
                            className="group flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-1.5 text-sm text-stone-800 data-disabled:cursor-default data-disabled:opacity-40 data-focus:bg-stone-100"
                          >
                            {ISO6391.getName(language)}
                            <CheckIcon className="invisible size-4 text-purple-600 group-data-selected:visible" />
                          </ComboboxOption>
                        ))}
                      </ComboboxOptions>
                    </Combobox>
                  </Field>
                </div>
                <label className={`${LABEL} mt-4`}>
                  Description
                  <textarea
                    rows={4}
                    value={draft.description ?? ''}
                    onChange={(event) => edit('description', event.target.value)}
                    className={`${TEXTAREA} mt-1.5`}
                  />
                </label>

                <div className="mt-6">
                  <p className={LABEL}>Ad pages</p>
                  <p className="mt-0.5 text-xs text-stone-500">The brand's own pages in the Meta Ad Library.</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    {draft.ad_pages.map((page) => (
                      <div
                        key={page.page_id}
                        className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-3"
                      >
                        <PageAvatar page={page} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-stone-900">{page.name}</p>
                          <a
                            href={`https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&view_all_page_id=${page.page_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-0.5 inline-flex items-center gap-1 text-xs text-stone-500 hover:text-purple-700"
                          >
                            View in Ad Library
                            <ArrowTopRightOnSquareIcon className="size-3" />
                          </a>
                        </div>
                        <button
                          type="button"
                          aria-label={`Remove ${page.name}`}
                          onClick={() => edit('ad_pages', draft.ad_pages.filter(({ page_id }) => page_id !== page.page_id))}
                          className={SMALL_REMOVE}
                        >
                          <XMarkIcon className="size-4" />
                        </button>
                      </div>
                    ))}

                    {draft.ad_pages.length < MAX_AD_PAGES && (
                      <button
                        type="button"
                        onClick={() => {
                          searchPages('')
                          setAddingPage(true)
                        }}
                        className={`${DASHED} min-h-16`}
                      >
                        <PlusIcon className="size-4" />
                        Add page
                      </button>
                    )}
                  </div>
                </div>

                {addingPage && (
                  <Modal title="Add ad page" width="max-w-md" padded={false} onClose={() => setAddingPage(false)}>
                    <div className="border-b border-stone-100 p-4">
                      <div className="relative">
                        <MagnifyingGlassIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-stone-400" />
                        <input
                          autoFocus
                          value={pageQuery}
                          onChange={(event) => searchPages(event.target.value)}
                          placeholder="Search the Meta Ad Library"
                          className={`${INPUT} pl-9`}
                        />
                      </div>
                    </div>
                    <div className="h-80 overflow-y-auto p-2 [scrollbar-width:thin]">
                      {searchingPages ? (
                        <p className="flex items-center justify-center gap-2 p-6 text-sm text-stone-500">
                          <Spinner className="size-3.5 border-stone-300 border-t-purple-600" />
                          Searching…
                        </p>
                      ) : pageMatches.length > 0 ? (
                        pageMatches.map((match) => {
                          const added = draft.ad_pages.some(({ page_id }) => page_id === match.page_id)
                          return (
                            <button
                              key={match.page_id}
                              type="button"
                              disabled={added}
                              onClick={() => {
                                edit('ad_pages', [
                                  ...draft.ad_pages,
                                  {
                                    page_id: match.page_id,
                                    name: match.name,
                                    facebook_page_url: match.facebook_page_url,
                                    profile_picture_url: match.profile_picture_url,
                                  },
                                ])
                                setAddingPage(false)
                              }}
                              className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-stone-100 disabled:opacity-50"
                            >
                              <PageAvatar page={match} />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-stone-900">{match.name}</span>
                                <span className="block truncate text-xs text-stone-500">
                                  {[match.category, match.likes && `${match.likes.toLocaleString()} likes`]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </span>
                              </span>
                              {added && <span className="shrink-0 text-xs font-medium text-purple-700">Added</span>}
                            </button>
                          )
                        })
                      ) : (
                        <p className="p-6 text-center text-sm text-stone-500">
                          {pageQuery.trim().length < MIN_QUERY
                            ? 'Type the name of the brand’s Facebook or Instagram page.'
                            : `No pages match "${pageQuery.trim()}".`}
                        </p>
                      )}
                    </div>
                  </Modal>
                )}
              </Section>

              <Section title="Look" hint="The colours, logos and visual style creatives should follow.">
                <p className={LABEL}>Colour theme</p>
                {draft.colours.length > 0 && (
                  <div className="mt-1.5 flex h-14 overflow-hidden rounded-2xl border border-stone-200">
                    {draft.colours.map((colour, index) => (
                      <div key={index} className={index === 0 ? 'flex-[2]' : 'flex-1'} style={{ backgroundColor: colour.hex }} />
                    ))}
                  </div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {draft.colours.map((colour, index) => {
                    const editColour = (patch: Partial<BrandColour>) =>
                      edit('colours', replaced(draft.colours, index, { ...colour, ...patch }))
                    return (
                      <div key={index} className="group relative overflow-hidden rounded-xl border border-stone-200 bg-white">
                        <label className="relative block h-24 cursor-pointer" style={{ backgroundColor: colour.hex }}>
                          <input
                            type="color"
                            value={colour.hex}
                            aria-label={`Pick ${colour.name || 'colour'}`}
                            onChange={(event) => editColour({ hex: event.target.value })}
                            className="absolute inset-0 size-full cursor-pointer opacity-0"
                          />
                        </label>
                        <div className="p-3">
                          <input
                            value={colour.name}
                            placeholder="Name"
                            onChange={(event) => editColour({ name: event.target.value })}
                            className={`${INLINE} text-sm font-medium text-stone-900`}
                          />
                          <input
                            value={colour.hex}
                            aria-label="Hex"
                            aria-invalid={!HEX.test(colour.hex)}
                            onChange={(event) => editColour({ hex: event.target.value })}
                            className={`${INLINE} text-xs text-stone-500 uppercase tabular-nums aria-invalid:text-red-600`}
                          />
                          <textarea
                            rows={2}
                            value={colour.usage}
                            placeholder="Where it's used"
                            onChange={(event) => editColour({ usage: event.target.value })}
                            className={`${INLINE} mt-1 resize-y text-xs leading-snug text-stone-600`}
                          />
                        </div>
                        <button
                          type="button"
                          aria-label="Remove colour"
                          onClick={() => edit('colours', removed(draft.colours, index))}
                          className={TILE_REMOVE}
                        >
                          <XMarkIcon className="size-4" />
                        </button>
                      </div>
                    )
                  })}
                  {draft.colours.length < MAX_COLOURS && (
                    <button
                      type="button"
                      onClick={() => edit('colours', [...draft.colours, { hex: NEW_COLOUR, name: '', usage: '' }])}
                      className={`${DASHED} min-h-44 flex-col`}
                    >
                      <PlusIcon className="size-5" />
                      Add colour
                    </button>
                  )}
                </div>

                <p className={`${LABEL} mt-8`}>Logos</p>
                <div className="mt-1.5 flex flex-wrap gap-3">
                  {draft.logos.map((logo, index) => (
                    <div
                      key={logo}
                      className={`group relative flex h-28 w-40 items-center justify-center rounded-xl border border-stone-200 p-4 ${CHECKERBOARD}`}
                    >
                      <img src={logo} alt={`Logo ${index + 1}`} className="max-h-full max-w-full object-contain" />
                      <button
                        type="button"
                        aria-label="Remove logo"
                        onClick={() => edit('logos', removed(draft.logos, index))}
                        className={TILE_REMOVE}
                      >
                        <XMarkIcon className="size-4" />
                      </button>
                    </div>
                  ))}
                  {draft.logos.length < MAX_LOGOS && (
                    <label className={`${DASHED} h-28 w-40 flex-col`}>
                      {uploading ? <Spinner className="size-4 border-stone-300 border-t-stone-500" /> : <ArrowUpTrayIcon className="size-5" />}
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploading}
                        onChange={(event) => {
                          const file = event.target.files?.[0]
                          event.target.value = ''
                          if (file) void uploadLogo(file)
                        }}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                <label className={`${LABEL} mt-8`}>
                  Style
                  <textarea
                    rows={4}
                    value={draft.style ?? ''}
                    placeholder="Photography, typography, layout and mood"
                    onChange={(event) => edit('style', event.target.value)}
                    className={`${TEXTAREA} mt-1.5`}
                  />
                </label>
              </Section>

              <Section title="Voice" hint="How the brand sounds, what it sells on, and who it talks to.">
                <label className={LABEL}>
                  Brand voice
                  <textarea
                    rows={4}
                    value={draft.voice ?? ''}
                    placeholder="Three sentences on how the brand writes"
                    onChange={(event) => edit('voice', event.target.value)}
                    className={`${TEXTAREA} mt-1.5`}
                  />
                </label>
                <TextList
                  label="Selling points"
                  items={draft.selling_points}
                  placeholder="What makes the brand worth choosing"
                  onChange={(items) => edit('selling_points', items)}
                />
                <TextList
                  label="Target audiences"
                  items={draft.audiences}
                  placeholder="Who the brand is for"
                  onChange={(items) => edit('audiences', items)}
                />
              </Section>

              <Section title="Competitors" hint="Who the brand competes with, and why it wins.">
                <div className="space-y-3">
                  {draft.competitors.map((competitor, index) => {
                    const editCompetitor = (patch: Partial<BookCompetitor>) =>
                      edit('competitors', replaced(draft.competitors, index, { ...competitor, ...patch }))
                    return (
                      <div key={index} className="relative rounded-xl border border-stone-200 bg-white p-4">
                        <button
                          type="button"
                          aria-label="Remove competitor"
                          onClick={() => edit('competitors', removed(draft.competitors, index))}
                          className={`${SMALL_REMOVE} absolute top-2 right-2`}
                        >
                          <XMarkIcon className="size-4" />
                        </button>
                        <div className="grid gap-x-3 gap-y-4 sm:grid-cols-2">
                          <label className={LABEL}>
                            Name
                            <input
                              value={competitor.name}
                              onChange={(event) => editCompetitor({ name: event.target.value })}
                              className={`${INPUT} mt-1.5 font-medium`}
                            />
                          </label>
                          <label className={LABEL}>
                            Website
                            <input
                              value={competitor.website ?? ''}
                              placeholder="https://"
                              onChange={(event) => editCompetitor({ website: event.target.value })}
                              className={`${INPUT} mt-1.5`}
                            />
                          </label>
                          <label className={LABEL}>
                            Where we compete
                            <textarea
                              rows={3}
                              value={competitor.where_we_compete ?? ''}
                              onChange={(event) => editCompetitor({ where_we_compete: event.target.value })}
                              className={`${TEXTAREA} mt-1.5`}
                            />
                          </label>
                          <label className={LABEL}>
                            Why we win
                            <textarea
                              rows={3}
                              value={competitor.why_we_win ?? ''}
                              onChange={(event) => editCompetitor({ why_we_win: event.target.value })}
                              className={`${TEXTAREA} mt-1.5`}
                            />
                          </label>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {draft.competitors.length < MAX_COMPETITORS && (
                  <button
                    type="button"
                    onClick={() => edit('competitors', [...draft.competitors, { name: '', website: '', where_we_compete: '', why_we_win: '' }])}
                    className={ADD}
                  >
                    <PlusIcon className="size-4" />
                    Add competitor
                  </button>
                )}
              </Section>

              <Section title="Extra info" hint="Anything else a copywriter should know.">
                <textarea
                  rows={4}
                  aria-label="Extra info"
                  value={draft.extra_info ?? ''}
                  placeholder="Pricing, offers, shipping, certifications…"
                  onChange={(event) => edit('extra_info', event.target.value)}
                  className={`${TEXTAREA} field-sizing-content min-h-24 [&:not([style*=height])]:max-h-72`}
                />
              </Section>
            </div>
          </>
        )}
      </div>

      {dirty && !generating && (
        <div className="sticky bottom-4 mx-auto mt-4 flex max-w-3xl items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white py-2.5 pr-2.5 pl-4 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)]">
          <p className={`text-sm max-sm:hidden ${badHex ? 'text-red-600' : 'text-stone-600'}`}>
            {badHex ? 'Colour codes need 6 digits, like #7C3AED' : 'Unsaved changes'}
          </p>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => setDraft(saved)}
              className="h-10 rounded-[10px] border border-stone-300 bg-white px-4 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50"
            >
              Discard
            </button>
            <button type="button" disabled={saving || uploading || badHex} onClick={() => void save()} className={PRIMARY}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
